import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Calendar, Building2, MessageSquare, Clock, History, User, Edit2, Save, X, BarChart3, GripVertical, LayoutGrid, Mail, Phone, MapPin, Copy, ExternalLink } from "lucide-react";
import { ColumnConfig } from "@/lib/columnConfig";
import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import LeadGanttChart from "./LeadGanttChart";
import EditHistoryDialog from "./EditHistoryDialog";
import { getCurrentUser } from "@/lib/authUser";

interface Lead {
  id: string;
  lead_name: string;
  company: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  priority: string;
  value: number | null;
  source: string | null;
  conversation_notes: string | null;
  next_action: string | null;
  next_action_date: string | null;
  created_at: string;
  updated_at?: string;
  custom_fields?: Record<string, any> | null;
  last_refreshed_at?: string | null;
  user_id?: string;
  group_id?: string | null;
}

interface LeadDetailsDialogProps {
  lead: Lead;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLeadUpdated: () => void;
  isGroupWorkspace?: boolean;
  columnConfig?: ColumnConfig[];
}

const LeadDetailsDialog = ({
  lead,
  open,
  onOpenChange,
  onLeadUpdated,
  isGroupWorkspace = false,
  columnConfig = []
}: LeadDetailsDialogProps) => {
  const [creatorName, setCreatorName] = useState<string | null>(null);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [userNotes, setUserNotes] = useState(lead.conversation_notes || "");
  const [isSaving, setIsSaving] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [panelWidth, setPanelWidth] = useState(540);
  const isDragging = useRef(false);

  // Notify FloatingAIChat of the panel width whenever it changes or opens/closes
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('lead-panel-width-change', { detail: open ? panelWidth : 0 }));
  }, [open, panelWidth]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    const startX = e.clientX;
    const startWidth = panelWidth;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const delta = startX - e.clientX;
      const newWidth = Math.min(Math.max(startWidth + delta, 320), window.innerWidth * 0.8);
      setPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [panelWidth]);

  useEffect(() => {
    const loadCurrentUser = async () => {
      const user = await getCurrentUser();
      setCurrentUserId(user?.id || null);
    };
    loadCurrentUser();
  }, []);

  useEffect(() => {
    if (open) {
      if (lead.user_id) {
        fetchCreatorName();
      }
      setUserNotes(lead.conversation_notes || "");
      setIsEditingNotes(false);
    }
  }, [open, lead.user_id, lead.id, lead.conversation_notes]);

  const fetchCreatorName = async () => {
    if (!lead.user_id) return;

    const { data } = await supabase
      .from("profiles")
      .select("full_name")
      .eq("user_id", lead.user_id)
      .maybeSingle();

    if (data?.full_name) {
      setCreatorName(data.full_name);
    }
  };

  const handleSaveNotes = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("leads")
        .update({ conversation_notes: userNotes })
        .eq("id", lead.id);

      if (error) throw error;

      toast.success("Notes saved successfully");
      setIsEditingNotes(false);
      onLeadUpdated();
    } catch (error) {
      console.error("Error saving notes:", error);
      toast.error("Failed to save notes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setUserNotes(lead.conversation_notes || "");
    setIsEditingNotes(false);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      new: "bg-blue-500/10 text-blue-500",
      contacted: "bg-yellow-500/10 text-yellow-500",
      qualified: "bg-purple-500/10 text-purple-500",
      proposal: "bg-orange-500/10 text-orange-500",
      negotiation: "bg-cyan-500/10 text-cyan-500",
      won: "bg-green-500/10 text-green-500",
      lost: "bg-red-500/10 text-red-500",
      do_not_contact: "bg-rose-500/10 text-rose-500 line-through"
    };
    return colors[status] || "bg-gray-500/10 text-gray-500";
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      low: "bg-gray-500/10 text-gray-500",
      medium: "bg-yellow-500/10 text-yellow-500",
      high: "bg-red-500/10 text-red-500"
    };
    return colors[priority] || "bg-gray-500/10 text-gray-500";
  };

  // ---- Custom field presentation -------------------------------------------
  const customFields = (lead.custom_fields || {}) as Record<string, any>;

  const customColumnNames = columnConfig
    .filter(c => c.type !== 'default' && !c.id.startsWith('default-'))
    .map(c => c.name);

  const orderedKeys = [
    ...customColumnNames.filter(n => {
      const v = customFields[n];
      return v !== undefined && v !== null && String(v).trim() !== "";
    }),
    ...Object.keys(customFields).filter(
      k => !customColumnNames.includes(k) &&
        customFields[k] !== undefined &&
        customFields[k] !== null &&
        String(customFields[k]).trim() !== ""
    ),
  ];

  const isEmailValue = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const isPhoneValue = (v: string) => /^[+()\d][\d\s().-]{6,}$/.test(v);
  const isUrlValue = (v: string) => /^https?:\/\//i.test(v);
  const isLongValue = (v: string) => v.length > 60 || v.includes("\n");

  const fieldIcon = (label: string, value: string) => {
    const l = label.toLowerCase();
    if (isEmailValue(value) || l.includes("email")) return Mail;
    if (isPhoneValue(value) || l.includes("phone") || l.includes("cell") || l.includes("mobile")) return Phone;
    if (l.includes("address") || l.includes("city") || l.includes("state") || l.includes("zip") || l.includes("apt")) return MapPin;
    return null;
  };

  const copyValue = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Could not copy");
    }
  };

  const renderFieldValue = (label: string, raw: any) => {
    const value = String(raw);
    if (isEmailValue(value)) {
      return (
        <a href={`mailto:${value}`} className="text-primary hover:underline break-all inline-flex items-center gap-1">
          {value}
        </a>
      );
    }
    if (isUrlValue(value)) {
      return (
        <a href={value} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all inline-flex items-center gap-1">
          {value}
          <ExternalLink className="h-3 w-3 flex-shrink-0" />
        </a>
      );
    }
    if (isPhoneValue(value)) {
      return (
        <a href={`tel:${value.replace(/[^\d+]/g, "")}`} className="text-primary hover:underline break-all">
          {value}
        </a>
      );
    }
    return <span className="break-words whitespace-pre-wrap">{value}</span>;
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
        <SheetContent side="right" className="overflow-y-auto overflow-x-hidden p-0 shadow-2xl" style={{ width: `${panelWidth}px`, maxWidth: '80vw', minWidth: '320px' }} onInteractOutside={(e) => e.preventDefault()}>
          {/* Resize handle — pill centred exactly on panel left edge */}
          <div
            onMouseDown={handleMouseDown}
            className="fixed top-1/2 -translate-y-1/2 cursor-col-resize z-50 group"
            style={{ left: `calc(100vw - ${panelWidth}px)`, transform: 'translateX(-50%) translateY(-50%)' }}
          >
            <div className="flex items-center justify-center w-5 h-14 rounded-full bg-muted border border-border group-hover:bg-primary/15 group-hover:border-primary/40 group-active:bg-primary/20 transition-all shadow-md">
              <GripVertical className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
          </div>
          <div className="p-6">
          <SheetHeader>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 flex-wrap">
                <SheetTitle className="text-2xl break-all">{lead.lead_name}</SheetTitle>
                {lead.company && (
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <Building2 className="h-3.5 w-3.5" />
                    {lead.company}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Created: {formatDate(lead.created_at)}
                  {creatorName && (
                    <span className="flex items-center gap-1 ml-1">
                      <User className="h-3 w-3" />
                      by {creatorName}
                    </span>
                  )}
                </span>
                {lead.updated_at && lead.updated_at !== lead.created_at && (
                  <span className="flex items-center gap-1">
                    • Updated: {formatDate(lead.updated_at)}
                  </span>
                )}
              </div>
            </div>
          </SheetHeader>
          
          <div className="space-y-4 w-full min-w-0 mt-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex gap-2 flex-wrap">
                <Badge className={getStatusColor(lead.status)}>
                  {lead.status?.replace(/_/g, " ")}
                </Badge>
                <Badge className={getPriorityColor(lead.priority)}>
                  {lead.priority} priority
                </Badge>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setHistoryDialogOpen(true)}
                className="h-8"
              >
                <History className="h-4 w-4 mr-1" />
                Edit History
              </Button>
            </div>

            <Separator />

            {/* Custom fields — everything from this sheet's own columns */}
            {orderedKeys.length > 0 && (
              <>
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <LayoutGrid className="h-5 w-5 text-muted-foreground" />
                      <Label>Lead Profile</Label>
                    </div>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                      {orderedKeys.length} field{orderedKeys.length === 1 ? "" : "s"}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {orderedKeys.map((key) => {
                      const value = String(customFields[key]);
                      const Icon = fieldIcon(key, value);
                      const wide = isLongValue(value);
                      return (
                        <div
                          key={key}
                          className={`group relative rounded-lg border border-border/60 bg-secondary/20 hover:bg-secondary/40 hover:border-primary/30 transition-colors p-3 min-w-0 ${wide ? "sm:col-span-2" : ""}`}
                        >
                          <div className="flex items-center gap-1.5 mb-1">
                            {Icon && <Icon className="h-3 w-3 text-muted-foreground flex-shrink-0" />}
                            <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground truncate">
                              {key}
                            </span>
                          </div>
                          <div className="text-sm font-medium pr-6 min-w-0">
                            {renderFieldValue(key, value)}
                          </div>
                          <button
                            type="button"
                            onClick={() => copyValue(key, value)}
                            aria-label={`Copy ${key}`}
                            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-muted-foreground hover:text-primary"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <Separator />
              </>
            )}

            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <MessageSquare className="h-5 w-5 text-muted-foreground" />
                  <Label>
                    {isGroupWorkspace ? "Group Notes" : "User Notes"}
                  </Label>
                  {isGroupWorkspace && (
                    <span className="text-xs text-muted-foreground">(Editable by all members)</span>
                  )}
                </div>
                {!isEditingNotes && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditingNotes(true)}
                    className="h-8"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {isEditingNotes ? (
                <div className="space-y-2">
                  <Textarea
                    value={userNotes}
                    onChange={(e) => setUserNotes(e.target.value)}
                    placeholder={isGroupWorkspace ? "Add notes that all group members can see and edit..." : "Add your personal notes about this lead..."}
                    className="min-h-[120px] resize-none"
                  />
                  <div className="flex flex-wrap gap-2 justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveNotes}
                      disabled={isSaving}
                    >
                      <Save className="h-4 w-4 mr-1" />
                      {isSaving ? "Saving..." : "Save"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-sm whitespace-pre-wrap bg-secondary/30 p-4 rounded-lg min-h-[80px]">
                  {userNotes || <span className="text-muted-foreground italic">No notes yet. Click edit to add notes.</span>}
                </div>
              )}
            </div>

            {(lead.next_action || lead.next_action_date) && (
              <>
                <Separator />
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-muted-foreground" />
                    <Label>Next Action</Label>
                  </div>
                  {lead.next_action && <p className="text-sm font-medium">{lead.next_action}</p>}
                  {lead.next_action_date && (
                    <p className="text-sm text-muted-foreground">
                      Due: {formatDate(lead.next_action_date)}
                    </p>
                  )}
                </div>
              </>
            )}

            <Separator />
            
            {/* Gantt Chart Progress Tracker */}
            {currentUserId && (
              <div className="space-y-3 w-full min-w-0">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                  <Label>Sandbox Progress Tracker</Label>
                </div>
                <div className="w-full min-w-0">
                  <LeadGanttChart 
                    leadId={lead.id} 
                    leadName={lead.lead_name}
                    userId={currentUserId} 
                    groupId={lead.group_id || null}
                  />
                </div>
              </div>
            )}
          </div>
          </div>
        </SheetContent>
      </Sheet>

      <EditHistoryDialog
        leadId={lead.id}
        leadName={lead.lead_name}
        createdAt={lead.created_at}
        creatorId={lead.user_id}
        open={historyDialogOpen}
        onOpenChange={setHistoryDialogOpen}
      />
    </>
  );
};

export default LeadDetailsDialog;
